<template>
  <NuxtLayout :name="layout">
    <template v-if="displayedError">
      <Header />

      <Container>
        <ErrorBlock v-bind="displayedError" />
      </Container>
    </template>

    <RevForm
      v-if="form && !displayedError && !isSummaryStep()"
      :key="formStep"
      :initial-values="form"
      :validate="validators"
      @submit="submitStep"
    >
      <template #default="{ values, hasError, errors, isValid }">
        <Header
          :activeStepIndex="formStep"
          class="mb-16 md:mb-32"
          :hasBack="!isFirstStep"
          :hasSkip="!isFirstStep"
          :steps-count
          @back="handleGoToPreviousStep(values)"
          @home="trackGoToHome(formStep)"
          @skip="handleSkip"
        />

        <TheMainContent>
          <Container class="mb-96 md:h-auto">
            <RevCard
              class="flex w-full flex-col gap-32 p-16 md:min-h-[396px] md:flex-row md:gap-56 md:px-32 md:py-24"
            >
              <ProductSummary
                v-if="product"
                :averageRate
                :backboxGrade
                class="shrink-0"
                :product
              />

              <div class="grow overflow-hidden">
                <Title
                  v-if="product"
                  :model="product?.model"
                  :step-id="formStepId"
                />

                <template
                  v-for="question of questions"
                  :key="question.identifier"
                >
                  <QuestionMainRating
                    v-if="isMainRatingQuestion(question)"
                    :id="question.identifier"
                    v-model="values[question.identifier] as number"
                    :error-message="errors[question.identifier]"
                    :subtitle="question.subtitle"
                    :title="question.title"
                    @update:model-value="
                      (rate) => {
                        trackRateChange({
                          step: formStep,
                          rate,
                          question: question.identifier,
                        })
                      }
                    "
                  />

                  <QuestionTextarea
                    v-if="isTextareaQuestion(question)"
                    :id="question.identifier"
                    v-model="values[question.identifier] as string"
                    :error-message="errors[question.identifier]"
                  />

                  <QuestionRatingBar
                    v-if="isRatingQuestion(question)"
                    :id="question.identifier"
                    v-model="values[question.identifier] as number"
                    :error-message="errors[question.identifier]"
                    :icon="question.icon"
                    :title="question.title"
                    @update:model-value="
                      (rate) => {
                        trackRateChange({
                          step: formStep,
                          rate,
                          question: question.identifier,
                        })
                      }
                    "
                  />

                  <QuestionFileUpload
                    v-if="isFileUploadQuestion(question)"
                    :id="question.identifier"
                    v-model="attachments"
                    :icon="question.icon"
                    :initial-attachments="
                      values[question.identifier] as UploadedFileValue[]
                    "
                    :title="question.title"
                    @click="trackFilePicker({ step: formStep })"
                  />
                </template>

                <SubmitButton
                  class="mt-12 hidden md:block"
                  :disabled="disableButton({ isValid, hasError })"
                  :step-id="formStepId"
                />

                <LegalText class="mt-16 md:mt-24" :step-id="formStepId" />
              </div>
            </RevCard>
          </Container>

          <div
            class="bg-surface-default-low border-static-default-low shadow-short fixed bottom-0 z-10 w-full p-16 md:hidden"
          >
            <SubmitButton
              :disabled="disableButton({ isValid, hasError })"
              :step-id="formStepId"
            />
          </div>
        </TheMainContent>
      </template>
    </RevForm>

    <ReviewSummary
      v-if="product && isSummaryStep()"
      :averageRate
      :backboxGrade
      class="mb-56 mt-32"
      :form-step-id
      :go-to-first-step
      :product
    />
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useHead, useRuntimeConfig } from '#imports'
import { computed, ref, watch } from 'vue'

import { RevCard } from '@ds/components/Card'
import { RevForm } from '@ds/components/Form'

import type { UploadedFileValue } from '~/api/reviews/types/GetReviewCollectionResponse'
import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'

import ErrorBlock from '../review-collection/components/ErrorBlock/ErrorBlock.vue'

import Container from './components/Container/Container.vue'
import Header from './components/Header/Header.vue'
import LegalText from './components/LegalText/LegalText.vue'
import ProductSummary from './components/ProductSummary/ProductSummary.vue'
import QuestionFileUpload from './components/QuestionFileUpload/QuestionFileUpload.vue'
import QuestionMainRating from './components/QuestionMainRating/QuestionMainRating.vue'
import QuestionRatingBar from './components/QuestionRatingBar/QuestionRatingBar.vue'
import QuestionTextarea from './components/QuestionTextarea/QuestionTextarea.vue'
import ReviewSummary from './components/ReviewSummary/ReviewSummary.vue'
import SubmitButton from './components/SubmitButton/SubmitButton.vue'
import Title from './components/Title/Title.vue'
import { useReviewCollection as useNewReviewCollection } from './composables/useReviewCollection'
import { useReviewCollectionTracking } from './composables/useReviewCollectionTracking'
import type { ReviewForm, ReviewFormQuestion } from './models/review-form'

const { COMPANY_NAME } = useRuntimeConfig().public

// TODO: Have a proper translation key for the funnel title
useHead({ title: COMPANY_NAME })

const {
  data,
  displayedError,
  getReviewCollectionPromise,
  submitPending,
  formStep,
  averageRate,
  updatedAverageRate,
  questions,
  validators,
  form,
  layout,
  submitStep: _submitStep,
  buildFormForCurrentStep,
  isFirstStep,
  goToPreviousStep,
  goToNextStep,
  goToFirstStep,
  isSummaryStep,
} = useNewReviewCollection()

const {
  trackFilePicker,
  trackGoToPreviousStep,
  trackGoToHome,
  trackRateChange,
  trackSkipStep,
} = useReviewCollectionTracking()

const attachments = ref<File[]>([])

await getReviewCollectionPromise

// update the form when the step changes
watch([formStep], () => buildFormForCurrentStep(), { immediate: true })

const product = computed(() => data.value?.product)
const backboxGrade = computed(() => data.value?.salesOrderItem.backboxGrade)
const stepsCount = computed(() => data?.value?.steps.length)
const formStepId = computed(() =>
  data.value ? data.value.steps[formStep.value]?.identifier : null,
)

const isMainRatingQuestion = (question: ReviewFormQuestion) =>
  question.type === 'rating_bar' && question.identifier === 'review_main_rate'
const isTextareaQuestion = (question: ReviewFormQuestion) =>
  question.type === 'textarea'
const isRatingQuestion = (question: ReviewFormQuestion) =>
  question.type === 'rating_bar' && question.identifier !== 'review_main_rate'
const isFileUploadQuestion = (question: ReviewFormQuestion) =>
  question.type === 'file_upload'

async function submitStep(values: ReviewForm) {
  const postAverageRate = await _submitStep({
    formStepId: formStepId.value,
    values,
    attachments: attachments.value,
  })

  if (postAverageRate) {
    updatedAverageRate.value = postAverageRate
  }

  window.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
}

function handleSkip() {
  trackSkipStep(formStep.value)
  goToNextStep()
}

function handleGoToPreviousStep(values: ReviewForm) {
  trackGoToPreviousStep(formStep.value)
  goToPreviousStep(values)
}

function disableButton({
  isValid,
  hasError,
}: {
  isValid: boolean
  hasError: boolean | undefined
}) {
  if (submitPending.value) return true
  if (formStepId.value === 'attachments' && attachments.value.length === 0)
    return true
  if (hasError) return true
  if (!isValid) return true

  return false
}
</script>
